"use client";
export type QuizResultPayload = Record<string, unknown>;
export type OnboardingQuizPayload = {
  quizResult?: unknown;
  userInputs?: unknown;
  quizId?: string;
  quizType?: string;
};
export const ONBOARDING_QUIZ_CONTEXT_MARKER = "[[ONBOARDING_QUIZ_CONTEXT]]";

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== "object") {
    return null;
  }
  return value as Record<string, unknown>;
};

export const pickOnboardingQuizPayload = (
  payload: QuizResultPayload | null,
): OnboardingQuizPayload | null => {
  if (!payload || typeof payload !== "object") {
    return null;
  }
  const rawData = asRecord(payload.rawData);
  const picked: OnboardingQuizPayload = {
    quizResult: rawData?.quizResult,
    userInputs: rawData?.userInputs,
    quizId: typeof payload.quizId === "string" ? payload.quizId : undefined,
    quizType:
      typeof payload.quizType === "string" ? payload.quizType : undefined,
  };
  if (
    !picked.quizResult &&
    !picked.userInputs &&
    !picked.quizId &&
    !picked.quizType
  ) {
    return null;
  }
  return picked;
};

const isTraditionalChineseLocale = (locale?: string): boolean => {
  const lang = (locale || "en").toLowerCase();
  return (
    lang.includes("zh-hk") ||
    lang.includes("zh-tw") ||
    lang.includes("zh-hant") ||
    lang.includes("zh-mo")
  );
};

const buildBrandingPrompt = (
  quizId: string,
  useTraditionalChinese: boolean,
) => {
  if (useTraditionalChinese) {
    return `用戶已做了${quizId}小測試，收到收入、支出、儲蓄、風險偏好等具體數據，預設這些數據真實有效，首次回覆直接基於這些數據做財務分析，不再追問已測內容，只問必要的新問題。數據如下：`;
  }
  return `The user has completed the ${quizId} assessment and provided concrete data such as income, expenses, savings, and risk preference. Assume these data are valid. In the first reply, provide financial analysis directly based on these data. Do not ask again about information already covered by the test; only ask necessary new questions. Data:`;
};

const buildViralPrompt = (quizId: string, useTraditionalChinese: boolean) => {
  if (useTraditionalChinese) {
    return `用戶已做了${quizId}趣味小測試，結果附在後面，開場可輕鬆提及該結果以認可使用戶參與，但需說明其娛樂性質，隨後以專業口吻詢問基礎財務資訊（如收入、儲蓄等），引導進入正式體檢。數據如下：`;
  }
  return `The user has completed the ${quizId} fun quiz and the result is attached below. You may briefly mention the result at the opening to acknowledge participation, but clearly state that it is for entertainment. Then switch to a professional tone to ask for foundational financial information (such as income and savings) and guide the user into the formal assessment. Data:`;
};

export const buildOnboardingQuizContextSuffix = (
  payload: OnboardingQuizPayload,
  locale?: string,
): string => {
  const contextJson = JSON.stringify(payload);
  const quizId = payload.quizId ?? "quiz";
  const useTraditionalChinese = isTraditionalChineseLocale(locale);
  const prompt =
    payload.quizType === "viral"
      ? buildViralPrompt(quizId, useTraditionalChinese)
      : buildBrandingPrompt(quizId, useTraditionalChinese);
  return `\n\n${ONBOARDING_QUIZ_CONTEXT_MARKER}\n${prompt}${contextJson}`;
};
